import { useCallback, useEffect, useState } from "react";
import type {
  GoogleChairStatus,
  GoogleSettings,
  GoogleSyncStateName,
} from "@dentacore/shared-types";

import {
  confirmGoogleChair,
  pairGoogleChair,
  readGoogleSettings,
  setGoogleAddress,
  unpairGoogleChair,
} from "../api/google";
import { Modal } from "../ui/modal";
import styles from "./settings.module.css";
import { Toast } from "./toast";
import { describe, useWriter } from "./use-writer";

/**
 * Google Calendar sync — STORY-045c.
 *
 * ── The screen's whole job is to make "not syncing" impossible to miss ─────
 *
 * A chair can be not-syncing for two opposite reasons, and they need different
 * words in front of a person (G23). `AWAITING_CONFIRMATION` is a setup step
 * nobody finished and the remedy is to go and look; `BROKEN` is something that
 * worked and stopped, and the remedy is to re-pair. A single "off" badge
 * covering both would make the first look like the second and send an admin
 * hunting for a fault that does not exist.
 *
 * ── Why a human has to press Confirm ──────────────────────────────────────
 *
 * Google's ACL API returns 200 for a grant to an address that does not exist —
 * `practice@gmial.com` succeeds silently. DentaCore's own writes then keep
 * working, because the calendar genuinely exists and we genuinely own it. So a
 * typo looks perfectly healthy forever, and **the only instrument that can
 * detect it is somebody's eyes on their own calendar list** (G5). The button
 * says so rather than asking for a confirmation that looks like ceremony.
 */

/** What each state is called, and what to do about it. */
const STATES: Record<
  GoogleSyncStateName,
  { label: string; tone: "good" | "warn" | "bad" | "quiet"; note: string }
> = {
  ACTIVE: { label: "Syncing", tone: "good", note: "Bookings appear on this chair's calendar." },
  AWAITING_CONFIRMATION: {
    label: "Not syncing yet",
    tone: "warn",
    note: "Waiting for you to confirm you can see it in Google.",
  },
  BROKEN: {
    label: "Stopped",
    tone: "bad",
    note: "Its calendar was deleted, or DentaCore's access was removed. Bookings are unaffected.",
  },
  NOT_PAIRED: { label: "Off", tone: "quiet", note: "No calendar for this chair." },
  RETIRED: {
    label: "Retired",
    tone: "quiet",
    note: "The chair was retired and its calendar deleted.",
  },
};

export function GooglePanel(): React.JSX.Element {
  const { busy, toast, dismiss, fail, run } = useWriter();
  const [settings, setSettings] = useState<GoogleSettings | null>(null);
  const [draft, setDraft] = useState("");
  const [editing, setEditing] = useState(false);
  /**
   * Whether an address was set in *this* visit, which is what earns a dialog.
   *
   * Not derived from `AWAITING_CONFIRMATION` — chairs sit in that state for
   * days quite legitimately, and a dialog on every visit to Settings would be
   * trained away in a week. This is the one moment the reader has just done the
   * thing that makes looking worthwhile.
   */
  const [justSaved, setJustSaved] = useState(false);

  const reload = useCallback(async (signal?: AbortSignal) => {
    setSettings(await readGoogleSettings(signal));
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    reload(controller.signal).catch((cause: unknown) => {
      if (controller.signal.aborted) return;
      fail(describe(cause, "Could not read this clinic's Google settings."));
    });
    return () => controller.abort();
  }, [reload, fail]);

  /**
   * **What this says, and why it changed** (QA20-8, QA20-9).
   *
   * It used to say *"Calendars created. Confirm you can see them in Google."* —
   * which is true about our end and silent about theirs, and the client read it
   * as a promise that something would be visible immediately. Two things had to
   * happen that the sentence did not mention: Google sends an invitation, and
   * somebody has to accept it. The invitation was suppressed at the time
   * (QA20-8), so for a personal Gmail it never arrived and the calendars never
   * appeared at all.
   *
   * So the wording now names the email, names the wait, and says what to do —
   * and `justSaved` raises the prompt below, because a badge in a panel the
   * reader is about to navigate away from is not how you ask somebody to go and
   * look at another application.
   */
  const save = (): void => {
    const email = draft.trim();
    if (email === "") return;
    run(
      () => setGoogleAddress(email),
      reload,
      "Calendars created. Google is emailing " +
        email +
        " an invitation — accept it, then confirm.",
    );
    setEditing(false);
    setJustSaved(true);
  };

  if (settings === null) return <p className={styles.quiet}>Reading&hellip;</p>;

  // Said plainly rather than offering a field that cannot work.
  if (!settings.available) {
    return (
      /*
        **The title is "Not available", not "Google Calendar".** The pane above
        carries that as its own heading, and printing it again here rendered it
        twice at the same size — caught in the browser while splitting Settings
        into panes.
      */
      <section className={styles.card}>
        <div className={styles.cardHead}>
          <div>
            <h3 className={styles.cardTitle}>Not available on this installation</h3>
          </div>
        </div>
      </section>
    );
  }

  const unconfirmed = settings.chairs.filter((c) => c.state === "AWAITING_CONFIRMATION").length;
  /**
   * Chairs that have a calendar at all — the denominator a person recognises.
   *
   * Not `chairs.length`: a chair that is `NOT_PAIRED` or `RETIRED` has nothing
   * to look for in Google, so counting it would ask somebody to find a
   * calendar that was never created.
   */
  const confirmable = settings.chairs.filter(
    (c) => c.state === "AWAITING_CONFIRMATION" || c.state === "ACTIVE",
  ).length;

  /**
   * Confirm every chair still waiting, in one go.
   *
   * **One act of looking, one button.** The API confirms a chair at a time and
   * that is the right shape for it — the partial case is real — but a person
   * who has just read their own calendar list has learned about all of them at
   * once, and asking for ten identical clicks to record one observation is how
   * the observation stops happening.
   */
  const confirmAll = (): void => {
    const waiting = settings.chairs.filter((c) => c.state === "AWAITING_CONFIRMATION");
    if (waiting.length === 0) return;
    run(
      // Sequential rather than `Promise.all`: each confirm is a write the
      // server audits, and a burst of ten from one click is a thundering herd
      // for no gain — this is a setup step, not a hot path.
      async () => {
        for (const chair of waiting) await confirmGoogleChair(chair.chairId);
      },
      reload,
      `${String(waiting.length)} chair${waiting.length === 1 ? "" : "s"} now syncing to Google.`,
    );
  };

  return (
    /*
      **"The account", not "Google Calendar".** The pane's own head carries the
      product name and what it sends; this card is about the one thing it holds
      — which Google account owns the calendars.
    */
    <section className={styles.card}>
      <div className={styles.cardHead}>
        <div>
          <h3 className={styles.cardTitle}>The account</h3>
        </div>
        {settings.workspaceEmail !== null && !editing && (
          <button
            type="button"
            className={styles.ghost}
            onClick={() => {
              setDraft(settings.workspaceEmail ?? "");
              setEditing(true);
            }}
            disabled={busy}
          >
            Change account
          </button>
        )}
      </div>

      {(settings.workspaceEmail === null || editing) && (
        <div className={styles.cardBody}>
          <label className={styles.field}>
            <span className={styles.label}>Your practice&rsquo;s Google account</span>
            <input
              className={styles.input}
              type="email"
              value={draft}
              placeholder="practice@yourclinic.com"
              onChange={(event) => setDraft(event.target.value)}
              data-testid="google-email"
            />
          </label>
          {/* The warning is here, where the decision is made, rather than in a
              help page nobody opens. A personal Gmail works perfectly and then
              takes the calendars with it when that person leaves (G2). */}
          <div className={styles.formActions}>
            <button
              type="button"
              className={styles.primary}
              onClick={save}
              disabled={busy || draft.trim() === ""}
              data-testid="google-save"
            >
              {settings.workspaceEmail === null ? "Create the calendars" : "Move to this account"}
            </button>
            {editing && (
              <button
                type="button"
                className={styles.ghost}
                onClick={() => setEditing(false)}
                disabled={busy}
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      )}
      {/* Which account owns the calendars, and the address DentaCore writes as,
          are no longer printed here (FX12) — the client treats both as
          confidential. *Change account* still opens the field. */}

      {unconfirmed > 0 && (
        /**
         * ── The setup step, said once and given weight ────────────────────
         *
         * This was a grey sentence in the same `blockNote` as the two
         * paragraphs above it, followed by one primary button per chair. Three
         * things were wrong with that and all three made people click through
         * without looking, which is the one outcome the step exists to prevent:
         *
         *   · It never said **why**. Without the reason, confirming ten times
         *     reads as ceremony, and ceremony gets clicked.
         *   · It told you to open Google Calendar and gave you no way to.
         *   · It modelled one act of looking as N independent facts. Somebody
         *     who has just looked at their calendar list has learned about all
         *     of them at once.
         *
         * So: the reason first, the looking as the primary action, and
         * confirming as the quieter one beside it — a person who has not
         * looked has to take the less prominent path to get past this.
         */
        <div className={styles.setup} data-testid="google-unconfirmed">
          <div className={styles.setupHead}>
            <p className={styles.setupTitle}>One step left</p>
            <span className={styles.setupCount} data-testid="google-progress">
              {confirmable - unconfirmed} of {confirmable} confirmed
            </span>
          </div>
          <p className={styles.setupBody}>
            We created {confirmable} calendar{confirmable === 1 ? "" : "s"} and handed{" "}
            {confirmable === 1 ? "it" : "them"} to{" "}
            <strong>{settings.workspaceEmail ?? "your practice account"}</strong>. Google emails
            that address an invitation, and{" "}
            <strong>the calendars only appear once it is accepted</strong> — which can take a few
            minutes to arrive.{" "}
            <strong>Google also accepts an address even when it is mistyped</strong>, and tells
            nobody — so the only way to know {confirmable === 1 ? "it" : "they"} arrived is for you
            to look.
          </p>
          <div className={styles.setupActions}>
            <a
              className={styles.primary}
              href="https://calendar.google.com/calendar/u/0/r/settings/calendars"
              target="_blank"
              rel="noreferrer"
              data-testid="google-open"
            >
              Open Google Calendar
            </a>
            <button
              type="button"
              className={styles.setupConfirm}
              onClick={confirmAll}
              disabled={busy}
              data-testid="google-confirm-all"
            >
              I can see {unconfirmed === 1 ? "it" : `all ${String(unconfirmed)}`}
            </button>
          </div>
        </div>
      )}

      {settings.chairs.length === 0 ? (
        <p className={styles.blank}>No chairs yet. Add one above and it will pair itself.</p>
      ) : (
        <ol className={styles.cardRows}>
          {settings.chairs.map((chair) => (
            <ChairRow
              key={chair.chairId}
              chair={chair}
              busy={busy}
              paired={settings.workspaceEmail !== null}
              onConfirm={() =>
                run(() => confirmGoogleChair(chair.chairId), reload, `${chair.label} is syncing.`)
              }
              onUnpair={() =>
                run(
                  () => unpairGoogleChair(chair.chairId),
                  reload,
                  `${chair.label} no longer syncs. Its calendar is still yours.`,
                )
              }
              onPair={() =>
                run(() => pairGoogleChair(chair.chairId), reload, `${chair.label} has a calendar.`)
              }
            />
          ))}
        </ol>
      )}

      {/*
        **The prompt the client asked for** (QA20-8): *"a side nav can go
        unseen."*

        Raised once, at the only moment it is worth raising — the reader has
        just set the address, so the calendars exist and nobody has looked at
        them yet. It is deliberately not driven by `unconfirmed > 0`: chairs sit
        unconfirmed for days quite legitimately, and a dialog on every visit to
        Settings is a dialog people learn to dismiss without reading.

        Dismissing it changes nothing — the "One step left" panel above is still
        there, and the chairs are still not syncing. This is a nudge with a link
        in it, not a gate.
      */}
      {justSaved && unconfirmed > 0 && (
        <Modal
          label="Check your Google calendars"
          onClose={() => setJustSaved(false)}
          testId="google-check-dialog"
        >
          <h2 className={styles.blockTitle}>Now check Google</h2>
          {/* A count and an address the server reported — data, not prose. */}
          <p className={styles.blockNote}>
            {confirmable} calendar{confirmable === 1 ? "" : "s"}{" "}
            {confirmable === 1 ? "was" : "were"} created and handed to{" "}
            <strong>{settings.workspaceEmail}</strong>.
          </p>
          <div className={styles.formActions}>
            <a
              className={styles.primary}
              href="https://calendar.google.com/calendar/u/0/r/settings/calendars"
              target="_blank"
              rel="noreferrer"
              data-testid="google-check-open"
            >
              Open Google Calendar
            </a>
            <button
              type="button"
              className={styles.ghost}
              onClick={() => setJustSaved(false)}
              data-testid="google-check-later"
            >
              I&rsquo;ll check in a minute
            </button>
          </div>
        </Modal>
      )}

      {toast !== null && <Toast kind={toast.kind} message={toast.message} onDismiss={dismiss} />}
    </section>
  );
}

function ChairRow({
  chair,
  busy,
  paired,
  onConfirm,
  onUnpair,
  onPair,
}: {
  chair: GoogleChairStatus;
  busy: boolean;
  paired: boolean;
  onConfirm: () => void;
  onUnpair: () => void;
  onPair: () => void;
}): React.JSX.Element {
  const state = STATES[chair.state];

  return (
    <li className={styles.row} data-testid="google-chair" data-state={chair.state}>
      <div className={styles.rowBody}>
        <div className={styles.rowTitle}>
          <span className={styles.rowName}>{chair.label}</span>
          <span className={styles.syncPill} data-tone={state.tone}>
            {state.label}
          </span>
        </div>
        <span className={styles.rowMeta}>{state.note}</span>
      </div>
      <div className={styles.rowActions}>
        {chair.state === "AWAITING_CONFIRMATION" && (
          <button
            type="button"
            /*
             * Quiet, not primary. Ten primary buttons down the page made the
             * column of them the loudest thing on the screen and the callout
             * above the quietest — the opposite of the order they should be
             * read in. This is the partial case; confirming all of them is the
             * ordinary one.
             *
             * **But not `.ghost` either.** As a ghost it was pixel-identical to
             * "Stop syncing" in the same row — one turns syncing on, the other
             * turns it off, and across ten rows only the words told them apart.
             * `.rowConfirm` keeps ghost's metrics exactly, so the rows stay
             * level, and differs in ink and boundary alone.
             */
            className={styles.rowConfirm}
            onClick={onConfirm}
            disabled={busy}
            data-testid="google-confirm"
          >
            I can see it
          </button>
        )}
        {(chair.state === "ACTIVE" || chair.state === "BROKEN") && (
          <button type="button" className={styles.ghost} onClick={onUnpair} disabled={busy}>
            Stop syncing
          </button>
        )}
        {chair.state === "NOT_PAIRED" && paired && (
          <button type="button" className={styles.ghost} onClick={onPair} disabled={busy}>
            Pair
          </button>
        )}
      </div>
    </li>
  );
}
